<script setup lang="ts">
import { cn } from '@/utils/utils'
import { Button } from '@/components/ui/button'

import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DateFormatter,
  type DateValue,
  getLocalTimeZone,
  parseDate,
} from '@internationalized/date'
import { CalendarIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import { DateFieldInput, DateFieldRoot } from 'reka-ui'
import { computed, nextTick, ref, watch, type HTMLAttributes } from 'vue'

defineOptions({ inheritAttrs: false })

const df = new DateFormatter('en-US', {
  dateStyle: 'long',
})

// The model is an ISO date string (YYYY-MM-DD), or null for no date. Every
// caller stores it that way, so the field and the calendar convert to and from
// a DateValue here and nowhere else.
const model = defineModel<string | null>()

// `label` names the field for assistive technology when a form has more than
// one picker ("Start date", "End date").
// `min` is the earliest day, as YYYY-MM-DD. An end date picker gets its start
// date here. The calendar greys out earlier days. A typed earlier date is still
// saved, and the field turns red; the form's own check decides whether it can
// be kept.
// `compact` drops the clear and calendar buttons, for a row with little room
// such as the filter builder's "is between". Clicking the field opens the
// calendar instead, and clicking the picked day again clears the date.
const props = defineProps<{
  label?: string
  min?: string | null
  disabled?: boolean
  compact?: boolean
  class?: HTMLAttributes['class']
}>()

function parseIsoDate(value: string | null | undefined): DateValue | undefined {
  if (!value) return undefined
  try {
    return parseDate(value)
  } catch {
    // A value that is not a plain ISO date shows as an empty field.
    return undefined
  }
}

const savedDate = computed(() => parseIsoDate(model.value))
const minValue = computed(() => parseIsoDate(props.min))

// The calendar picks a whole day, so it writes the model directly. Clicking the
// selected day again gives undefined, which clears the date.
const calendarDate = computed<DateValue | undefined>({
  get: () => savedDate.value,
  set: (value) => {
    writeModel(value ? value.toString() : null)
    if (props.compact) calendarOpen.value = false
  },
})

const calendarOpen = ref(false)
const fieldRef = ref<HTMLElement | null>(null)

function onFieldClick() {
  if (props.compact && !props.disabled) calendarOpen.value = true
}

// A compact calendar opens without taking focus, so typing in the field still
// works while it is open. Clicking or moving between the field's parts must not
// close it either. Anything else outside the calendar closes it as usual.
function onCalendarOpenAutoFocus(event: Event) {
  if (props.compact) event.preventDefault()
}

function onCalendarInteractOutside(event: Event) {
  if (props.compact && fieldRef.value?.contains(event.target as Node)) {
    event.preventDefault()
  }
}

const calendarButtonName = computed(() => {
  const shown = savedDate.value ? df.format(savedDate.value.toDate(getLocalTimeZone())) : 'not set'
  return props.label ? `${props.label}: ${shown}` : `Open calendar: ${shown}`
})

// The typed field is reka-ui's DateField. It writes a new date on every
// keystroke once every part holds a number, so typing the year 2027 passes
// through the years 2, 20 and 202, and emptying a part writes undefined. None of
// that may reach the model. So the field is given the saved date one way, and
// what it writes is held here until the date is complete:
// - pendingDate is the last complete date the field wrote that is not saved yet.
//   A date is complete when the month, the day and the year all hold a number
//   and the year has four digits. Emptying any part clears pendingDate.
// - fieldDirty is true while the field shows something other than the saved date.
// - fieldKey remounts the field, which redraws it from the saved date.
const pendingDate = ref<DateValue | null>(null)
const fieldDirty = ref(false)
const fieldKey = ref(0)

function writeModel(next: string | null) {
  pendingDate.value = null
  fieldDirty.value = false
  if (next !== (model.value ?? null)) {
    model.value = next
  }
}

function commitPending() {
  if (pendingDate.value) {
    writeModel(pendingDate.value.toString())
  }
}

function onFieldUpdate(value: DateValue | undefined) {
  fieldDirty.value = true
  pendingDate.value = value && value.year >= 1000 ? value : null
}

// The digits typed into the day in a row, kept to clamp a day that is too large
// for the month (see onFieldKeydown).
const typedDayDigits = ref('')

// The part of the date an element is: 'month', 'day', 'year' or a literal.
function segmentPart(element: EventTarget | null) {
  return (element as HTMLElement | null)?.getAttribute('data-reka-date-field-segment') ?? null
}

function onFieldFocusin(event: FocusEvent) {
  // The field starts the day over when the cursor comes back to it, so the count
  // starts over too. Focus moving on to the year must not reset it: the field
  // moves the cursor before this component reads the second digit.
  if (segmentPart(event.target) === 'day') {
    typedDayDigits.value = ''
  }
}

// Two digits typed in a row that make a day too large for the month, such as 3
// then 1 in November, make the field start the day over at the second digit. The
// spec clamps that day to the month's last day instead. Any other key in the day
// breaks the run, because the field also starts over after it: 3, Backspace, 5
// is the 5th, not 35.
function clampTypedDay(key: string) {
  if (!/^[0-9]$/.test(key)) {
    typedDayDigits.value = ''
    return
  }
  typedDayDigits.value += key
  if (typedDayDigits.value.length < 2) return

  const typedDay = Number(typedDayDigits.value)
  typedDayDigits.value = ''
  if (!pendingDate.value) return
  const daysInMonth = pendingDate.value.calendar.getDaysInMonth(pendingDate.value)
  if (typedDay > daysInMonth) {
    pendingDate.value = pendingDate.value.set({ day: daysInMonth })
  }
}

// This runs after the field's own key handling, because the event bubbles up
// from the part to the field. By then the field has written its new date.
// - Up and down step a part to a real date, so they are saved at once when every
//   part holds a number. In an empty field they fill only the part the cursor is
//   in, with today's month, day or year, and nothing is saved.
// - A digit that finishes a day or a month moves the cursor to the next part.
//   Until then the part may still be waiting for a second digit: the "1" of a
//   "15" is held, so retyping a day writes it once.
// - A digit in the year is saved as soon as the year has four digits.
// - A part can change on screen without the field writing anything: a leading 0
//   in the month or the day empties it, and up or down in an empty field fills
//   one part. So a digit, Backspace, up or down marks the field as edited by
//   itself, and a held date is only saved while every part still holds a number.
//   Which parts are empty shows on the page after the next render, so the check
//   waits for it.
// - A day typed too large for the month is clamped first (see clampTypedDay).
// - Escape closes a compact calendar. The field cancels every key it handles,
//   Escape too, and the calendar ignores a cancelled Escape, so it never closes
//   by itself while the cursor is in the field.
function onFieldKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.compact && calendarOpen.value) {
    calendarOpen.value = false
    return
  }
  const field = event.currentTarget as HTMLElement
  const isStep = event.key === 'ArrowUp' || event.key === 'ArrowDown'
  const isDigit = /^[0-9]$/.test(event.key)
  const part = segmentPart(event.target)
  const finishedPart = isDigit && document.activeElement !== event.target
  const inYear = isDigit && part === 'year'
  if (isDigit || isStep || event.key === 'Backspace') {
    fieldDirty.value = true
  }
  if (part === 'day') {
    clampTypedDay(event.key)
  }
  nextTick(() => {
    if (hasEmptyPart(field)) {
      pendingDate.value = null
    } else if (isStep || finishedPart || inYear) {
      commitPending()
    }
  })
}

function hasEmptyPart(field: HTMLElement) {
  return field.querySelector('[data-reka-date-field-segment][data-placeholder]') !== null
}

// Moving between parts fires this too, before the field writes its new date, so
// it only acts once focus has settled outside the field. Leaving saves a complete
// date. Leaving an incomplete one redraws the field from the saved date, so the
// field never shows a date the form does not hold.
function onFieldFocusout(event: FocusEvent) {
  const field = event.currentTarget as HTMLElement
  nextTick(() => {
    if (field.contains(document.activeElement)) return
    if (pendingDate.value && !hasEmptyPart(field)) {
      commitPending()
    } else if (fieldDirty.value) {
      fieldDirty.value = false
      fieldKey.value++
    }
  })
}

function clear() {
  writeModel(null)
}

// A date set from outside, such as a form reset, replaces whatever is half typed.
// The field redraws itself for a new date, but it keeps half-typed parts when the
// date goes to null, so that case remounts it.
watch(model, (next) => {
  pendingDate.value = null
  fieldDirty.value = false
  if (!next) fieldKey.value++
})
</script>

<template>
  <Popover v-model:open="calendarOpen">
    <div
      ref="fieldRef"
      v-bind="$attrs"
      :class="cn(
        'border-input dark:bg-input/30 relative flex h-9 w-full max-w-[280px] min-w-0 items-center gap-1 rounded-md border bg-transparent pl-3 pr-1 text-sm shadow-xs transition-[color,box-shadow]',
        'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
        'has-[[role=group][data-invalid]]:border-destructive',
        props.compact && 'cursor-pointer px-2',
        props.disabled && 'cursor-not-allowed opacity-50',
        props.class,
      )"
      data-test="date-picker-field"
      @click="onFieldClick"
    >
      <!-- A compact calendar hangs under the whole field, since it has no button. -->
      <PopoverAnchor v-if="props.compact" class="pointer-events-none absolute inset-0" />
      <DateFieldRoot
        v-slot="{ segments }"
        :key="fieldKey"
        :model-value="savedDate ?? null"
        :min-value="minValue"
        :disabled="props.disabled"
        locale="en-US"
        :aria-label="props.label"
        class="flex min-w-0 flex-1 items-center tabular-nums"
        @update:model-value="onFieldUpdate"
        @focusin="onFieldFocusin"
        @keydown="onFieldKeydown"
        @focusout="onFieldFocusout"
      >
        <template v-for="(item, index) in segments" :key="`${item.part}-${index}`">
          <DateFieldInput
            v-if="item.part === 'literal'"
            :part="item.part"
            class="text-muted-foreground px-px"
          >
            {{ item.value }}
          </DateFieldInput>
          <DateFieldInput
            v-else
            :part="item.part"
            class="focus:bg-accent focus:text-accent-foreground data-[placeholder]:text-muted-foreground rounded-sm px-0.5 outline-none"
          >
            {{ item.value }}
          </DateFieldInput>
        </template>
      </DateFieldRoot>
      <Button
        v-if="!props.compact && savedDate && !props.disabled"
        type="button"
        variant="ghost"
        size="icon"
        class="text-muted-foreground size-7 shrink-0"
        data-test="date-picker-clear"
        :aria-label="props.label ? `Clear ${props.label.toLowerCase()}` : 'Clear date'"
        @click="clear"
      >
        <XMarkIcon class="h-4 w-4" />
      </Button>
      <PopoverTrigger v-if="!props.compact" as-child>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          class="text-muted-foreground size-7 shrink-0"
          data-test="date-picker-calendar"
          :disabled="props.disabled"
          :aria-label="calendarButtonName"
        >
          <CalendarIcon class="h-4 w-4" />
        </Button>
      </PopoverTrigger>
    </div>
    <PopoverContent
      class="w-auto p-0"
      @open-auto-focus="onCalendarOpenAutoFocus"
      @interact-outside="onCalendarInteractOutside"
    >
      <Calendar v-model="calendarDate" :min-value="minValue" :initial-focus="!props.compact" />
    </PopoverContent>
  </Popover>
</template>
